import { currencyUtilities } from '@incentivegames/ig-frontend-common/lib/utilities/currencyUtilities';
import { communicationUtilities } from '@incentivegames/ig-frontend-common/lib/utilities/communicationUtilities';
import { EventStatus } from '@incentivegames/ig-types/lib/enums/event/eventStatus';
import { IGame, ITeamMember, IEvent, IGameEntry } from '@incentivegames/ig-types/lib/interfaces/apiContracts';

import { getEntries, getPrize } from 'store/actions/dreamteamActions';
import { dreamteamUtilities } from 'utilities/dreamteamUtilities';
import { GameHeaderState } from 'constants/appEnums';
import { IInitialState } from 'interfaces/gameInterfaces';
import config from 'theme/config';

interface IGetCurrentGameHeaderStateArgs {
  game?: IGame;
  entry?: IGameEntry;
  events: IEvent[] | undefined;
  readyToRevealPlayer: boolean;
  currentTime: number;
  formattedNextPlayerTime: string;
  futureGame: boolean;
  prizeAmount: number | undefined;
  revealAnimationInProgress: boolean | undefined;
  loading: boolean;
  hidePrize: boolean;
}

interface ICreateReadyPlayerRevealCountDownArgs {
  activeTeamMember: ITeamMember | undefined;
  selectedGameId: number | undefined;
  token: string | undefined;
  setFormattedNextPlayerTime: (timeString: string) => void;
  dispatch: any;
}

interface ICreateReadyPlayerExpireCountDown {
  activeTeamMember: ITeamMember | undefined;
  selectedEntry: IGameEntry | undefined;
  token: string | undefined;
  selectedGameId: number | undefined;
  setActiveTeamMember: (activeTeamMember: ITeamMember | undefined) => void;
  dispatch: any;
}

interface ICreateFutureGameCountDown {
  revealTime: number | undefined;
  selectedGame: IGame | undefined;
  setFormattedStartTime: (starTime: string) => void;
}

export const getFormattedStartTime = (t: any, gameStartTime: number) => {
  let gameStartTimeTemp = gameStartTime - Date.now();
  const days = Math.floor(gameStartTimeTemp / (1000 * 60 * 60 * 24));
  const hours = Math.floor((gameStartTimeTemp % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((gameStartTimeTemp % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((gameStartTimeTemp % (1000 * 60)) / 1000);

  if (gameStartTimeTemp < 0) {
    return '';
  } else {
    return `${days === 0 ? '' : days + 'd'} ${hours === 0 && days === 0 ? '' : hours + t('h')} ${minutes === 0 && hours === 0 && days === 0 ? '' : minutes + t('m')} ${seconds} ${t('s')}`;
  }
};

export const isPlayerReadyToRevealed = (activeTeamMember: ITeamMember) => {
  const currentTime = Date.now();
  return currentTime > activeTeamMember.revealTime && currentTime < activeTeamMember.expiryTime && !activeTeamMember.revealed;
};

export const getActiveTeamMember = (team: ITeamMember[]) => {
  let activeTeamMember = undefined;
  const currentTime = Date.now();

  for (let i = team.length - 1; i >= 0; i--) {
    if (currentTime < team[i].revealTime || (currentTime > team[i].revealTime && currentTime < team[i].expiryTime && !team[i].revealed)) {
      activeTeamMember = team[i];
    }
  }
  if (!activeTeamMember || team[team.length - 1].revealed || team[team.length - 1].expiryTime < currentTime) {
    activeTeamMember = team[team.length - 1];
  }
  return activeTeamMember;
};

export const getCurrentGameHeaderState = (options: IGetCurrentGameHeaderStateArgs) => {
  const { game, readyToRevealPlayer, currentTime, entry, events, formattedNextPlayerTime, futureGame, prizeAmount, revealAnimationInProgress, loading, hidePrize } = options;

  if (futureGame) return GameHeaderState.futureGame;
  if (entry === undefined && !loading) return GameHeaderState.missedGame;
  if (readyToRevealPlayer && !loading) return GameHeaderState.playerReady;

  const summary = events && dreamteamUtilities.getEventSummary(events);
  const gameHasStarted = game ? currentTime > game.startTime : false;
  const firstMatchHasStarted = summary?.firstStartTime ? currentTime > summary.firstStartTime : gameHasStarted;

  if (formattedNextPlayerTime !== '' && !firstMatchHasStarted) return GameHeaderState.playerReadySoon;

  const hasPoints = entry && entry.totalPoints > 0;
  const hasBetCredited = hasPoints && entry && entry.returns !== 0;
  const allEventsFinished =
    firstMatchHasStarted &&
    !entry?.team.some((teamMember: ITeamMember) => {
      if (!teamMember.revealed) return false;
      const event = events?.find((event: IEvent) => event.eventId === teamMember.eventId);
      if (!event) return true;
      return event.eventPeriod?.toUpperCase() !== EventStatus.fullTime;
    });

  if (allEventsFinished && !loading) {
    if (hasPoints) {
      // Sometimes the user can have points but the bet takes a bit to be settled and credit the user
      // To avoid inconsistency we wait until we know the money was credited
      return hasBetCredited ? GameHeaderState.finishedWithPoint : GameHeaderState.gameInPlayOrReadyToPlay;
    }
    return GameHeaderState.finishedNoPoints;
  }

  if (prizeAmount && !hidePrize && !revealAnimationInProgress) return GameHeaderState.gameInPlayOrReadyToPlay;

  return GameHeaderState.placeholder;
};

export const getCurrency = (currencyCode: string) => {
  return currencyUtilities.getCurrencyByCode(currencyCode || config.settings.defaultCurrency);
};

export const isFutureWeek = (selectedGame?: IGame) => {
  const timeNow = new Date().getTime();
  return !!selectedGame && selectedGame.openTime > timeNow;
};

export const getCurrentGame = (dreamteamReducer: IInitialState | undefined) => {
  return dreamteamReducer?.games?.find((game: IGame) => game.gameId === dreamteamReducer?.selectedGameId);
};

export const handleSelectedGame = (dreamteamReducer: IInitialState, setSelectedGame: (currentGame: IGame) => void) => {
  if (!dreamteamReducer.selectedGameId || !dreamteamReducer.games) return;
  const currentGame = getCurrentGame(dreamteamReducer);
  if (currentGame) {
    setSelectedGame(currentGame);
  }
};

export const handleActiveTeamMember = (dreamteamReducer: IInitialState, setActiveTeamMember: (activeTeamMember: ITeamMember | undefined) => void) => {
  if (dreamteamReducer.selectedEntry?.team && dreamteamReducer.games) {
    const activeTeamMember = getActiveTeamMember(dreamteamReducer.selectedEntry.team);
    setActiveTeamMember(activeTeamMember);
  } else {
    setActiveTeamMember(undefined);
  }
};

export const handleBalance = (dreamteamReducer: IInitialState) => {
  if (dreamteamReducer.selectedEntry && dreamteamReducer.selectedEntry?.returns > 0 && !dreamteamReducer.selectedEntry?.settled) communicationUtilities.sendPostMessage.updateBalance();
};

export const createReadyPlayerRevealCountDown = (t: any, options: ICreateReadyPlayerRevealCountDownArgs) => {
  const setCountDown = options.activeTeamMember && !isPlayerReadyToRevealed(options.activeTeamMember);
  if (!setCountDown) return null;

  return setInterval(() => {
    if (options.activeTeamMember) {
      options.setFormattedNextPlayerTime(getFormattedStartTime(t, options.activeTeamMember.revealTime));
      if (isPlayerReadyToRevealed(options.activeTeamMember) && options.token && options.selectedGameId) {
        options.dispatch(getEntries(options.token, options.selectedGameId));
      }
    }
  }, 1000);
};

export const createReadyPlayerExpireCountDown = (options: ICreateReadyPlayerExpireCountDown) => {
  const setCountDown = options.activeTeamMember && !isPlayerReadyToRevealed(options.activeTeamMember);
  if (!setCountDown) return null;

  return setInterval(() => {
    if (options.activeTeamMember && options.activeTeamMember.expiryTime > new Date().getTime() && options.token && options.selectedGameId) {
      const newActive = options.selectedEntry && getActiveTeamMember(options.selectedEntry.team);

      if (newActive) {
        options.setActiveTeamMember(newActive);
      } else {
        options.dispatch(getEntries(options.token, options.selectedGameId));
        if (options.selectedEntry) options.dispatch(getPrize(options.token, options.selectedEntry.receipt));
      }
    }
  }, 1000);
};

export const createFutureGameCountDown = (t: any, options: ICreateFutureGameCountDown) => {
  if (options.revealTime) {
    const isFutureGame = isFutureWeek(options.selectedGame);
    const firstScheduledPlayerRevealTime = options.revealTime;
    if (isFutureGame && firstScheduledPlayerRevealTime) {
      return setInterval(() => {
        options.setFormattedStartTime(getFormattedStartTime(t, firstScheduledPlayerRevealTime));
      }, 1000);
    }
  }
};
